import { AccountSelect, TaxRateSelect } from '@components'

import { Flex, FormItemsGroup, IconButton, Input, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ImportedInvoiceReviewFormSchema } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'
import { getImportedInvoiceLineNumber } from '@features/importedInvoices/utils/getImportedInvoiceLineNumber'
import { AccountNature } from '@modules-deprecated/app/accounts/types'

import { useFormContext, useWatch } from '../../../../../../../../../../hooks/useForm'
import { ImportedInvoiceReviewAmountField } from './elements/ImportedInvoiceReviewAmountField'
import { ImportedInvoiceReviewGrossAmountField } from './elements/ImportedInvoiceReviewGrossAmountField'
import { ImportedInvoiceReviewVatAmountField } from './elements/ImportedInvoiceReviewVatAmountField'
import { useImportedInvoiceReviewLineAmounts } from './hooks/useImportedInvoiceReviewLineAmounts'

const CATEGORY_NATURES = [AccountNature.Revenue]

interface ImportedInvoiceReviewLineProps {
  isReadOnly: boolean
  lineIndex: number
  onRemove: (lineIndex: number) => void
  withName: boolean
}

export const ImportedInvoiceReviewLine = ({
  isReadOnly,
  lineIndex,
  onRemove,
  withName,
}: ImportedInvoiceReviewLineProps): ReactElement => {
  const { t } = useTranslation()
  const { control, formState, FormItem } = useFormContext<ImportedInvoiceReviewFormSchema>()
  const currency = useWatch({ control, name: 'currency' })
  const { grossAmount, vatAmount } = useImportedInvoiceReviewLineAmounts({ currency, lineIndex })

  const isDisabled = isReadOnly || formState.isSubmitting
  const lineNumber = getImportedInvoiceLineNumber(lineIndex)

  const handleRemoveClick = useCallback(() => {
    onRemove(lineIndex)
  }, [lineIndex, onRemove])

  return (
    <Flex flexDirection="column" gap={Spacing.S16}>
      {withName && (
        <Flex alignItems="center" justifyContent="space-between">
          <Text as="h3" variant="bodyMD" weight="bold">
            {t('imported_invoices.review.form.line', { number: lineNumber })}
          </Text>
          {!isReadOnly && (
            <IconButton
              aria-label={t('imported_invoices.review.form.delete_line', { number: lineNumber })}
              disabled={isDisabled}
              icon="trash"
              onClick={handleRemoveClick}
            />
          )}
        </Flex>
      )}

      {/* `TaxRateSelect` is a plain function component, so it drops the field's `ref` rather than
          take one it cannot hold. `AccountSelect` and `Input` forward theirs. */}
      <FormItemsGroup itemsInRow={1}>
        <FormItem
          label={t('imported_invoices.review.form.description')}
          name={`lines.${lineIndex}.description`}
          render={({ field }) => <Input {...field} disabled={isDisabled} />}
        />
        <FormItem
          label={t('imported_invoices.review.form.category')}
          name={`lines.${lineIndex}.categoryId`}
          render={({ field: { onChange, value, ...field } }) => (
            <AccountSelect
              {...field}
              disabled={isDisabled}
              natures={CATEGORY_NATURES}
              onSelect={(categoryId) => onChange(categoryId ?? null)}
              selectedId={value ?? undefined}
            />
          )}
        />
      </FormItemsGroup>

      <FormItemsGroup itemsInRow={1}>
        <ImportedInvoiceReviewAmountField
          isReadOnly={isReadOnly}
          label={t('imported_invoices.review.form.amount_excl_vat')}
          name={`lines.${lineIndex}.amountExclVat`}
          withCurrencyPicker={lineIndex === 0}
        />
      </FormItemsGroup>

      <FormItemsGroup itemsInRow={2}>
        <FormItem
          label={t('imported_invoices.review.form.vat_rate')}
          name={`lines.${lineIndex}.vatRateId`}
          render={({ field: { onChange, ref, value, ...field } }) => (
            <TaxRateSelect
              {...field}
              disabled={isDisabled}
              hasShortItemTitle
              onSelect={(vatRateId) => onChange(vatRateId ?? null)}
              sale
              selectedId={value ?? undefined}
            />
          )}
        />
        <ImportedInvoiceReviewVatAmountField
          currency={currency}
          label={t('imported_invoices.review.form.vat_amount')}
          value={vatAmount}
        />
      </FormItemsGroup>

      <FormItemsGroup itemsInRow={1}>
        <ImportedInvoiceReviewGrossAmountField
          currency={currency}
          grossAmount={grossAmount}
          label={t('imported_invoices.review.form.amount_incl_vat')}
        />
      </FormItemsGroup>
    </Flex>
  )
}
